import OpenInNew from "@mui/icons-material/OpenInNew";
import { Stack, Typography } from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import IconButton from "@mui/material/IconButton";
import { CustomLink } from "core/components/CustomLink";
import { pickHighlight, renderHighlight } from "core/components/HighlightText";
import { useFormatDate } from "core/hooks/useFormatDate";
import { formatDmsDocumentLink } from "features/DMS/components/DMSWidget/formatDmsDocumentLink";
import { usePreloadDmsNode } from "features/DMS/hooks/usePreloadDmsNode";
import { MyLinksIcon } from "features/MyLinks/components/MyLinksIcon";
import { useTranslation } from "react-i18next";
import type { SearchResultDocument } from "../defs";
import { SearchItemTemplate } from "./SearchItemTemplate";

export function SearchItemDocument({
  dynamicData,
  title,
  blurb,
  isFavorited,
  highlights,
}: SearchResultDocument) {
  const formatDate = useFormatDate({ dateStyle: "short" });
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:SearchItem" });
  const dateLabel = t("publishedTime");
  const formattedDate = formatDate(dynamicData.publishedTime);
  const preloadNode = usePreloadDmsNode();
  const docNodeDocumentHref = formatDmsDocumentLink(dynamicData.path);

  const breadCrumbs =
    dynamicData.parentBreadCrumbs.map((breadCrumb, _i) => ({
      name: breadCrumb.title,
      path: breadCrumb.path,
      href:
        breadCrumb.type === "Library" || breadCrumb.type === "Folder"
          ? `/dms/library/v2${breadCrumb.path}`
          : null,
    })) ?? [];

  const parentBreadCrumbs = (
    <Breadcrumbs separator=">">
      {breadCrumbs.map(({ href, path, name }) =>
        href ? (
          <CustomLink
            sx={{ lineHeight: 1 }}
            variant="caption"
            fontWeight={500}
            key={path}
            href={href}
            onPointerOver={() => {
              preloadNode(path!, "Library", "view");
            }}
          >
            {name}
          </CustomLink>
        ) : (
          <Typography sx={{ lineHeight: 1 }} key={path} variant="overline">
            {name}
          </Typography>
        )
      )}
      <span />
    </Breadcrumbs>
  );

  const titleEl = (
    <Stack>
      {parentBreadCrumbs}
      <CustomLink variant="body1" fontWeight="bold" href={docNodeDocumentHref}>
        {renderHighlight(title, pickHighlight(highlights, "String1"))}
      </CustomLink>
    </Stack>
  );

  return (
    <SearchItemTemplate
      data-testid="search-item-dms-document-link"
      dateLabel={dateLabel}
      dateText={formattedDate}
      AvatarIcon={<MyLinksIcon itemType="DmsDocNodeDocument" />}
      secondaryAction={
        <IconButton href={docNodeDocumentHref} LinkComponent={CustomLink}>
          <OpenInNew color="primary" />
        </IconButton>
      }
      title={titleEl}
      middleSlot={renderHighlight(blurb, pickHighlight(highlights, "String5"))}
      isFavorited={isFavorited}
    />
  );
}
